import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from '../UI/Index';
import inventoryService from '../../services/inventoryService';

const AddProductModal = ({ isOpen, onClose, inventoryId, onProductAdded, inventoryType }) => {
  const [step, setStep] = useState(1); // 1: Código producto, 2: Cantidad
  const [productCode, setProductCode] = useState('');
  const [productSPUPhoto, setProductSPUPhoto] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [productName, setProductName] = useState('');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  const resetModal = () => {
    setStep(1);
    setProductCode('');
    setProductSPUPhoto('');
    setQuantity('');
    setLoading(false);
    setProductName('');
    setError('');
    setSuccess('');
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleProductCodeChange = (e) => {
    setProductCode(e.target.value.trim());
  };

const handleQuantityChange = (e) => {
  let value = e.target.value;

  // Solo permitir números
  if (value === '' || /^\d+$/.test(value)) {
    // Limitar a máximo 3 caracteres
    if (value.length > 4) {
      value = value.slice(0, 4);
    }
    setQuantity(value);
  }
};


  const handleCheckProduct = async (e) => {
    e.preventDefault();
    setError('');

    if (!productCode) {
      setError('Debes ingresar un código de producto');
      return;
    }

    try {
      setLoading(true);
      const response = await inventoryService.checkProduct(productCode);

      if (response.success && response.exists) {
        setProductName(response.product_name || 'Nombre del producto no disponible');
        setProductSPUPhoto(response.image ?? null);
        //        console.log("objeto" + response.product_name);
        //        console.log("foto respuesta" + response.product_name)
        //        console.log("Foto variable:" + productSPUPhoto);
        setStep(2);
      } else {
        setError('El producto no existe en Polarik');
      }
    } catch (err) {
      setError(err.message || 'Error al verificar el producto');
    } finally {
      setLoading(false);
    }
  };

  const processAddWithForce = async (quantityToAdd) => {
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      const detailData = {
        inventoryId: inventoryId,
        productId: productCode,
        qtyInputPicked: quantityToAdd,
        requiresValidation: 0,
        forceCreate: true
      };

      const response = await inventoryService.createInventoryDetail(detailData);

      if (response.success) {
        setSuccess(inventoryType === 'expired'
          ? 'Caducado añadido correctamente al registro'
          : 'Producto añadido correctamente al inventario'
        );

        setTimeout(() => {
          onProductAdded();
          handleClose();
        }, 1500);
      } else {
        setError(response.message || 'Error al añadir el producto');
      }
    } catch (err) {
      setError(err.message || 'Error al añadir el producto');
    } finally {
      setLoading(false);
    }
  };

const handleAddProduct = async (e) => {
  e.preventDefault();
  setError('');
  setSuccess('');

  if (!quantity) {
    setError('Debes ingresar una cantidad');
    return;
  }

  const numQuantity = parseInt(quantity, 10);
  if (isNaN(numQuantity) || numQuantity < 0) {
    setError('La cantidad debe ser un número positivo');
    return;
  }

  if (numQuantity > 4000) {
    setError('La cantidad máxima permitida es 4000 unidades');
    return;
  }

  try {
    setLoading(true);

    const detailData = {
      inventoryId: inventoryId,
      productId: productCode,
      qtyInputPicked: numQuantity,
      requiresValidation: 0
    };

    const response = await inventoryService.createInventoryDetail(detailData);

    if (!response.success && response.show_modal_validation) {
      setConfirmationData({
        quantity: numQuantity,
        message: response.message,
        currentStock: response.currentStock,
        userQuantity: response.userQuantity,
        productCost: response.productCost
      });
      setShowConfirmation(true);
      setLoading(false);
      return;
    }

    if (response.success) {
      setSuccess(inventoryType === 'expired'
        ? 'Caducado añadido correctamente al registro'
        : 'Producto añadido correctamente al inventario'
      );

      setTimeout(() => {
        onProductAdded();
        handleClose();
      }, 1500);
    } else {
      setError(response.message || 'Error al añadir el producto');
    }
  } catch (err) {
    setError(err.message || 'Error al añadir el producto');
  } finally {
    setLoading(false);
  }
};

  const handleConfirmQuantity = async () => {
    setShowConfirmation(false);
    await processAddWithForce(confirmationData.quantity);
    setConfirmationData(null);
  };

  const handleCancelCorrection = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setError('');
  };

  const handleMainModalClose = () => {
    if (!showConfirmation) {
      handleClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleMainModalClose}
        title={inventoryType === 'expired' ? 'Añadir Caducado' : 'Añadir Producto'}
        size="md"
      >
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} />}

        <div className="mt-4">
          {step === 1 && (
            <div>
              <p className="mb-4 text-sm text-gray-600">
                {inventoryType === 'expired'
                  ? 'Introduce el código del producto caducado que deseas registrar.'
                  : 'Introduce el código del producto que deseas añadir al inventario.'}
              </p>

              <form onSubmit={handleCheckProduct}>
                <div className="mb-4">
                  <label htmlFor="productCode" className="block text-sm font-medium text-gray-700">
                    Código del Producto *
                  </label>
                  <input
                    type="text"
                    id="productCode"
                    value={productCode}
                    onChange={handleProductCodeChange}
                    required
                    className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ingrese el código del producto"
                    disabled={loading}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button type="secondary" onClick={handleClose} disabled={loading}>
                    Cancelar
                  </Button>
                  <Button type="primary" buttonType="submit" disabled={loading || !productCode}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Verificando...
                      </>
                    ) : (
                      'Verificar Producto'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="p-4 mb-4 border border-green-200 bg-green-50 rounded-md">
                <h3 className="text-lg font-medium text-green-800 mb-2">
                  ✓ El producto existe en Polarik
                </h3>
                <div className="flex justify-center my-3">
                  {productSPUPhoto === null ? (
                    <b className="my-auto">Cargando foto...</b>
                  ) : productSPUPhoto ? (
                    <img
                      src={productSPUPhoto}
                      alt={productName}
                      className="w-32 h-32 object-cover rounded-md border border-gray-300 bg-white"
                    />
                  ) : (
                    <b className="my-auto">El producto no tiene imagen asociada</b>
                  )}
                </div>
                <p className="text-sm text-green-700">
                  <span className="font-medium"><b>Nombre:</b></span> {productName}
                </p>
                <p className="text-sm text-green-700">
                                    <span className="font-medium"><b>Código:</b></span> {productCode}

                </p>
              </div>

              <form onSubmit={handleAddProduct}>
                <div className="mb-4">
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                    {inventoryType === 'expired' ? 'Unidades caducadas *' : 'Cantidad a inventariar *'}
                  </label>
                  <input
                    type="text"
                    id="quantity"
                    value={quantity}
                    onChange={handleQuantityChange}
                    required
                    maxLength={4}
                    className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={inventoryType === 'expired' ? 'Unidades caducadas a retirar' : 'Ingrese la cantidad'}
                    autoFocus
                    disabled={loading}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button type="secondary" onClick={handleBackToStep1} disabled={loading}>
                    Volver
                  </Button>
                  <Button type="secondary" onClick={handleClose} disabled={loading}>
                    Cancelar
                  </Button>
                  <Button type="primary" className={inventoryType === 'expired' ? 'bg-orange-500 hover:bg-orange-600' : ''} buttonType="submit" disabled={loading || success !== ''}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Añadiendo...
                      </>
                    ) : (
                      inventoryType === 'expired' ? 'Añadir Caducado' : 'Añadir Producto'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal>


      <Modal
        isOpen={showConfirmation}
        onClose={() => { }}
        title="Diferencia Importante Detectada"
        size="md"
      >
        <div className="mt-4 mb-6">
          <div className="p-4 mb-4 border border-amber-200 bg-amber-50 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Diferencia importante detectada
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>{confirmationData?.message}</p>
                  <p className="mt-1">
                    <strong>¿Estás seguro que deseas añadir este producto con esta cantidad?</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="secondary"
              onClick={handleCancelCorrection}
              disabled={loading}
            >
              Cancelar y corregir
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmQuantity}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Añadiendo...
                </>
              ) : (
                'Confirmar cantidad'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddProductModal;